import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Home, LayoutGrid, ArrowLeft, Building2 } from 'lucide-react';
import { CustomBreadcrumb, CustomInput, CustomButton } from '@/shared/components/atoms';
import { CustomCommonScreenHeader } from '@/shared/components/molecules';
import { departmentApi } from '../api/departmentApi';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';

const createDepartmentSchema = z.object({
    name: z.string().min(2, 'Department name must be at least 2 characters'),
});

type CreateDepartmentFormData = z.infer<typeof createDepartmentSchema>;

export const CreateDepartment = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [isLoading, setIsLoading] = useState(false);
    const { showToast } = useToastStore();

    const schoolSlug = user?.school?.slug || '';

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<CreateDepartmentFormData>({
        resolver: zodResolver(createDepartmentSchema),
    });

    const onSubmit = async (data: CreateDepartmentFormData) => {
        if (!schoolSlug) {
            showToast('School context not found', 'error');
            return;
        }

        setIsLoading(true);
        try {
            const response = await departmentApi.createDepartment(schoolSlug, data);
            if (response.success) {
                showToast('Department created successfully!', 'success');
                navigate(`/${schoolSlug}/school-admin/departments`);
            } else {
                showToast(response.error || 'Failed to create department', 'error');
            }
        } catch (error) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ padding: '1rem 1.5rem' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Departments', path: `/${schoolSlug}/school-admin/departments`, icon: <LayoutGrid size={14} /> },
                    { label: 'Create Department' }
                ]}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <CustomCommonScreenHeader
                    title="Create New Department"
                    subtitle="Define a new organizational unit for your school."
                    icon={<LayoutGrid size={24} />}
                />
            </div>

            <div style={{
                maxWidth: '600px',
                margin: '2rem auto',
                background: 'var(--card-bg)',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                padding: '2.5rem',
                boxShadow: 'var(--card-shadow)'
            }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        <CustomInput
                            label="Department Name"
                            placeholder="e.g. Computer Science, Mathematics"
                            leftIcon={<LayoutGrid size={18} />}
                            error={errors.name?.message}
                            {...register('name')}
                            required
                        />

                        <div style={{
                            marginTop: '2rem',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '1rem',
                            borderTop: '1px solid var(--border-color)',
                            paddingTop: '2rem'
                        }}>
                            <CustomButton
                                variant="outline"
                                color="secondary"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </CustomButton>
                            <CustomButton
                                type="submit"
                                variant="filled"
                                color="primary"
                                loading={isLoading}
                            >
                                Create Department
                            </CustomButton>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
